import { TeamPerson } from '@numeo/types-main-web-app'
import { ApiError, requestApi } from './axiosBaseQuery'
import { TEAM_MAX_IDS } from '@/utils/team/teamQuery'

/**
 * Names for ids, asked one id at a time and sent together: every id asked within `BATCH_WINDOW_MS` goes into one
 * `GET /team/people/v1?ids=` (at most 100 ids per request). A screen that names a row's driver and dispatcher in each of
 * fifty rows costs one request, not a hundred; and a board whose rows arrive over a few renders (each set of ids in its own
 * tick) still costs one, not one per render.
 */

/** How long ids are gathered before they are sent: a few renders' worth, well under what a person notices. */
const BATCH_WINDOW_MS = 25

/** The server's limit per request (`ids=` names at most a page's worth). */
const MAX_IDS_PER_REQUEST = TEAM_MAX_IDS

type PersonResult = { data: TeamPerson | null } | { error: ApiError }
type Resolve = (result: PersonResult) => void

let queue = new Map<string, Resolve[]>()
let scheduled = false

const send = async (ids: string[], waiting: Map<string, Resolve[]>): Promise<void> => {
    const result = await requestApi<{ items?: TeamPerson[] }>({ url: '/team/people/v1', params: { ids: ids.join(',') } })
    if ('error' in result) {
        for (const id of ids) for (const resolve of waiting.get(id) ?? []) resolve({ error: result.error })
        return
    }
    const found = new Map((result.data?.items ?? []).map((person) => [person.id, person]))
    // An id the server does not name (no such person in this company) is cached as nobody, not asked again each render.
    for (const id of ids) for (const resolve of waiting.get(id) ?? []) resolve({ data: found.get(id) ?? null })
}

const flush = (): void => {
    const waiting = queue
    queue = new Map()
    scheduled = false
    const ids = [...waiting.keys()]
    for (let start = 0; start < ids.length; start += MAX_IDS_PER_REQUEST) void send(ids.slice(start, start + MAX_IDS_PER_REQUEST), waiting)
}

/** The person with this id, sent with every other id asked in the same tick. Never rejects: a failure is an `error`. */
export const loadPerson = (id: string): Promise<PersonResult> =>
    new Promise((resolve) => {
        queue.set(id, [...(queue.get(id) ?? []), resolve])
        if (scheduled) return
        scheduled = true
        setTimeout(flush, BATCH_WINDOW_MS)
    })
